import React from "react";
import { Routes, Route, Navigate, Link } from "react-router-dom";
import { Loader2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import AppLayout from "@voult/components/AppLayout";
import Home from "@voult/pages/Home";
import VaultList from "@voult/pages/VaultList";
import AddCard from "@voult/pages/AddCard";
import TokenDetails from "@voult/pages/TokenDetails";
import ActivityLog from "@voult/pages/ActivityLog";
import VaultSettings from "@voult/pages/VaultSettings";
import UsageAnalytics from "@voult/pages/UsageAnalytics";
import BulkManagement from "@voult/pages/BulkManagement";
import SecurityAudit from "@voult/pages/SecurityAudit";
import IntegrationKeys from "@voult/pages/IntegrationKeys";
import PaymentHistory from "@voult/pages/PaymentHistory";
import SecurityDashboard from "@voult/pages/SecurityDashboard";
import MerchantAccess from "@voult/pages/MerchantAccess";
import SecurityCenter from "@voult/pages/SecurityCenter";
import ApiCredentials from "@voult/pages/ApiCredentials";
import Transactions from "@voult/pages/Transactions";
import Checkout from "@voult/pages/Checkout";

const PAGES = {
  "vault-list": VaultList, "add-card": AddCard, "token-details": TokenDetails, checkout: Checkout, activity: ActivityLog,
  settings: VaultSettings, "usage-analytics": UsageAnalytics, "bulk-management": BulkManagement, "security-audit": SecurityAudit,
  "integration-keys": IntegrationKeys, "payment-history": PaymentHistory, "security-dashboard": SecurityDashboard,
  "merchant-access": MerchantAccess, "security-center": SecurityCenter, "api-credentials": ApiCredentials, transactions: Transactions,
};

class Boundary extends React.Component {
  state = { error: null };
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="p-10 text-center" data-testid="voult-error">
        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
        <p className="text-slate-700">This screen hit an error.</p>
        <Link to="/voult/vault-list" onClick={() => this.setState({ error: null })} className="text-indigo-600 text-sm hover:underline">Back to My Vault</Link>
      </div>
    );
  }
}

// Voult card vault, mounted at /voult/* behind the CITIDAN login.
export default function VoultApp() {
  const { user } = useAuth();
  if (user === undefined) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }
  if (!user) return <Login />;
  return (
    <div className="voult-app bg-slate-50 text-slate-900" data-testid="voult-app">
      <Boundary>
        <Routes>
          <Route path="home" element={<Home />} />
          <Route element={<AppLayout />}>
            <Route index element={<Navigate to="vault-list" replace />} />
            {Object.entries(PAGES).map(([p, C]) => <Route key={p} path={p} element={<C />} />)}
            <Route path="*" element={<Navigate to="vault-list" replace />} />
          </Route>
        </Routes>
      </Boundary>
    </div>
  );
}
